
import React from "react";
import { Classname } from "@rivtn/noseur";
import { i18nManager } from "../i18n";
import { useInView } from "../hooks";
import SectionHeader from "./common/SectionHeader";
import AchievementCard from "./common/AchievementCard";
import styles from "../assets/style.module.css";
import { IProfileWebsite } from "../../../../services";
import { ACHIEVEMENTS, buildRankings, IAchievement, IRankingEntry } from "../data";

export interface AchievementProps {
    website: IProfileWebsite;
}

function AchievementGrid({ achievements }: { achievements: IAchievement[]; }) {
    const [ref, inView] = useInView();
    return (<div ref={ref} className={styles["honours"]}>
        {achievements.map((a, i) => (<AchievementCard key={a.title} achievement={a} index={i} visible={inView} />))}
    </div>);
}

function Rankings({ website }: AchievementProps) {

    const labels = i18nManager.Labels.achievements;
    const rankings = React.useMemo(() => buildRankings(website.display_name), [website.display_name]);
    const allGames = Array.from(new Set(rankings.map((r) => r.game)));
    const allCountries = ["All", ...Array.from(new Set(rankings.map((r) => r.country))).sort()];
    const [rankGame, setRankGame] = React.useState<string>(allGames[0]);
    const [rankCountry, setRankCountry] = React.useState<string>("All");
    const filtered: IRankingEntry[] = rankings
        .filter((r) => r.game === rankGame && (rankCountry === "All" || r.country === rankCountry))
        .sort((a, b) => a.rank - b.rank);
    const medal = (rank: number) => ((rank >= 1 && rank <= 3) ? (<i className="fa fa-medal" style={{ color: ["#F5C451", "#C0C7D1", "#CD7F32"][rank - 1] }} />) : rank);

    return (<>
        <div className={styles["rank-filters"]}>
            <select className={styles["rank-select"]} value={rankGame} onChange={(e) => setRankGame(e.target.value)}>
                {allGames.map((g) => (<option key={g} value={g}>{g}</option>))}
            </select>
            <select className={styles["rank-select"]} value={rankCountry} onChange={(e) => setRankCountry(e.target.value)}>
                {allCountries.map((c) => (<option key={c} value={c}>{c === "All" ? labels.all_countries : c}</option>))}
            </select>
            <span className={styles["rank-count"]}>{labels.teams_count.replace(":count", `${filtered.length}`)}</span>
        </div>

        <div className={styles["rank-table-wrap"]}>
            {filtered.length === 0 ? (<div className={styles["rank-empty"]}>{labels.no_teams}</div>) : (<table className={styles["rank-table"]}>
                <thead>
                    <tr>
                        <th style={{ width: 56 }}>#</th>
                        <th>{labels.team}</th>
                        <th className={Classname.build(styles["center"], styles["hide-mobile"])}>W</th>
                        <th className={Classname.build(styles["center"], styles["hide-mobile"])}>D</th>
                        <th className={Classname.build(styles["center"], styles["hide-mobile"])}>L</th>
                        <th className={styles["center"]}>Pts</th>
                        <th className={Classname.build(styles["center"], styles["hide-mobile"])}>{labels.form}</th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((entry, idx) => (<tr key={`${entry.team}-${entry.game}-${idx}`} className={Classname.build({ [styles["rank-ours"]]: entry.isOurs })}>
                        <td><span className={styles["rank-pos"]}>{medal(entry.rank)}</span></td>
                        <td>
                            <div className={styles["rank-team"]}>
                                <span className={styles["rank-flag"]}>{entry.flag}</span>
                                <div>
                                    <div className={styles["rank-team-name"]}>
                                        {entry.team}
                                        {entry.isOurs && (<span className={styles["current-badge"]}>{labels.us}</span>)}
                                    </div>
                                    <div className={styles["rank-team-tag"]}>{entry.country}</div>
                                </div>
                            </div>
                        </td>
                        <td className={Classname.build(styles["center"], styles["hide-mobile"])}>{entry.wins}</td>
                        <td className={Classname.build(styles["center"], styles["hide-mobile"])}>{entry.draws}</td>
                        <td className={Classname.build(styles["center"], styles["hide-mobile"])}>{entry.losses}</td>
                        <td className={styles["center"]}><span className={styles["rank-pts"]}>{entry.points}</span></td>
                        <td className={Classname.build(styles["center"], styles["hide-mobile"])}>
                            <span className={styles["form-row"]}>
                                {entry.form.map((f, i) => (<span key={i} className={Classname.build(styles["form-pill"], styles[`form-${f}`])}>{f}</span>))}
                            </span>
                        </td>
                    </tr>))}
                </tbody>
            </table>)}
        </div>

        <div className={styles["rank-legend"]}>
            {[labels.wins, labels.draws, labels.losses].map((l) => (<span key={l}>{l}</span>))}
            <span className={styles["c-pink"]}>{labels.points}</span>
            <span style={{ marginLeft: "auto" }}>
                <span className={styles["current-badge"]}>{i18nManager.Labels.highlighted}</span> = {website.display_name}
            </span>
        </div>
    </>);
}

function Achievement({ website }: AchievementProps) {

    const labels = i18nManager.Labels.achievements;
    const settings = website.achievement_settings ?? {} as IProfileWebsite["achievement_settings"];

    return (<div className={styles["page-inner"]}>
        {settings.show_trophies_graphs && (<section className={styles["section"]}>
            <div className={styles["container"]}>
                <SectionHeader as="h1" eyebrow={labels.eyebrow} title={labels.trophy_title} titleEm={labels.trophy_title_em} subtitle={labels.trophy_desc} />
                <AchievementGrid achievements={ACHIEVEMENTS} />
            </div>
        </section>)}

        {settings.show_team_trophies_graphs && (<section className={Classname.build(styles["section"], styles["section-dark"])}>
            <div className={styles["container"]}>
                <SectionHeader eyebrow={labels.eyebrow} title={labels.team_title} titleEm={labels.team_title_em} subtitle={labels.team_desc} />
                <AchievementGrid achievements={ACHIEVEMENTS} />
            </div>
        </section>)}

        {settings.show_leaderboard_trails && (<section className={Classname.build(styles["section"], styles["section-alt"])}>
            <div className={styles["container"]}>
                <SectionHeader eyebrow={labels.rankings_eyebrow} title={labels.rankings_title} titleEm={labels.rankings_title_em} subtitle={labels.rankings_desc.replace(":name", website.display_name)} />
                <Rankings website={website} />
            </div>
        </section>)}
    </div>);
}

export default Achievement;
